import { MediaGuideMetadata } from '@/utils/media/types';
import {
    buildFullShuffleOrder,
    buildShuffleOrder,
} from '@rhg/utils/arrays/shuffle';
import { getGuideId } from '@rhg/utils/media';
import {
    createContext,
    ReactNode,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react';
import { nextLoopMode } from './capabilities';
import { LoopCapability, LoopMode } from './types';

export interface SetQueueArgs {
    guides: MediaGuideMetadata[];
    capability: LoopCapability;
    /** Whether the content type supports shuffle (see shuffleCapabilityForControls). */
    isShuffleSupported?: boolean;
    /**
     * Open the queue already shuffled (the EVE-11935 playlist Shuffle CTA). When
     * true, a full random order is built immediately; the caller chooses the
     * starting guide. When false/omitted, loading a new context turns shuffle off.
     */
    willShuffle?: boolean;
    /** Distinguishes queue sources whose guide order could match. */
    sourceKey?: string;
}

export interface GuideQueueState {
    queue: MediaGuideMetadata[];
    loopMode: LoopMode;
    loopCapability: LoopCapability;
    isShuffled: boolean;
    isShuffleSupported: boolean;
    setQueue: (args: SetQueueArgs) => void;
    /**
     * Sync the queue index of the playing track. Stored in a ref (not exposed on
     * the context value) and read only by `toggleShuffle` to keep the current
     * track in place on enable — so per-track updates don't re-render consumers.
     */
    setCurrentIndex: (index: number) => void;
    resetLoop: () => void;
    /** Reset shuffle to off and clear the shuffle order. Called by the player on full close. */
    resetShuffle: () => void;
    cycleLoop: () => void;
    /**
     * Toggle shuffle. On enable the currently playing track moves to the start of
     * the shuffle order and the other tracks are randomised after it (so playback
     * is not interrupted and "next" can reach every remaining track — EVE-13081);
     * on disable the original `queue` order is restored.
     */
    toggleShuffle: () => void;
    /** Target index for "next" from currentIndex, or null to stop. PURE (read in render). */
    next: (currentIndex: number) => number | null;
    /** Target index for "previous" from currentIndex, or null to stop. PURE. */
    previous: (currentIndex: number) => number | null;
    /**
     * Like `next`, but when both shuffle and Loop-All are on and the last track
     * of the shuffled order has finished, it re-randomises into a brand-new order
     * and returns its first track. This mutates shuffle state, so it must be
     * called ONLY from event handlers (track-end / skip), never during render.
     */
    advanceNext: (currentIndex: number) => number | null;
}

const DEFAULT_STATE: GuideQueueState = {
    queue: [],
    loopMode: LoopMode.Off,
    loopCapability: LoopCapability.Unsupported,
    isShuffled: false,
    isShuffleSupported: false,
    setQueue: () => undefined,
    setCurrentIndex: () => undefined,
    resetLoop: () => undefined,
    resetShuffle: () => undefined,
    cycleLoop: () => undefined,
    toggleShuffle: () => undefined,
    next: () => null,
    previous: () => null,
    advanceNext: () => null,
};

export const GuideQueueContext = createContext<GuideQueueState>(DEFAULT_STATE);

/**
 * Owns the playback queue plus the loop and shuffle state for the audio player.
 *
 * Navigation model: `queue` is always the canonical, original track order and is
 * never reordered. Shuffle is represented by `shuffleOrder` — a permutation of
 * `queue` indices that is the *playback* order while shuffle is on (null when
 * off). So:
 *   - Not shuffled: `next`/`previous` step linearly through `queue` indices.
 *   - Shuffled: `next`/`previous` find the current index's position inside
 *     `shuffleOrder` and step within that permutation, mapping back to a `queue`
 *     index. Turning shuffle off just drops `shuffleOrder`, restoring the
 *     original order with the current track still playing.
 *
 * `next`/`previous` are pure reads (safe during render). The only navigation
 * helper that mutates is `advanceNext`, used on the track-advance event path.
 */
export const GuideQueueProvider = ({ children }: { children: ReactNode }) => {
    const [queue, setQueueState] = useState<MediaGuideMetadata[]>([]);
    const [loopMode, setLoopMode] = useState<LoopMode>(LoopMode.Off);
    const [loopCapability, setLoopCapability] = useState<LoopCapability>(
        LoopCapability.Unsupported
    );
    const [isShuffled, setIsShuffled] = useState(false);
    const [isShuffleSupported, setIsShuffleSupported] = useState(false);
    // Permutation of `queue` indices = the playback order while shuffle is on.
    const [shuffleOrder, setShuffleOrder] = useState<number[] | null>(null);
    // Playing track index lives in a ref, not state: only `toggleShuffle` reads
    // it, and updating it on every track change must not re-render consumers.
    const currentIndexRef = useRef(-1);
    const setCurrentIndex = useCallback((index: number) => {
        currentIndexRef.current = index;
    }, []);

    // Identity of the currently-loaded guide set. active-player re-runs setQueue
    // on every track change and on re-renders that hand it a fresh `sessions`
    // array (same content, new reference). We only treat a setQueue as a *new
    // context* when the guide ids actually change — otherwise shuffle/loop would
    // reset on play/pause/skip (EVE-11931).
    const queueSignatureRef = useRef<string>('');

    const setQueue = useCallback(
        ({
            guides,
            capability,
            isShuffleSupported: supported = false,
            willShuffle = false,
            sourceKey,
        }: SetQueueArgs) => {
            setQueueState(guides);
            setLoopCapability(capability);
            setIsShuffleSupported(supported);

            const signature = `${sourceKey ?? ''}\u0001${guides
                .map(getGuideId)
                .join('\0')}`;
            const isSameContext =
                guides.length > 0 && signature === queueSignatureRef.current;
            queueSignatureRef.current = signature;

            // Same album/playlist re-supplied (track change, play/pause,
            // re-render): preserve shuffle state — it must not reset.
            if (isSameContext) {
                return;
            }

            if (willShuffle && supported && guides.length > 0) {
                // EVE-11935: open already shuffled — full random order. Gated on
                // `supported` so shuffle nav can't engage while the control is hidden.
                setShuffleOrder(buildFullShuffleOrder(guides.length));
                setIsShuffled(true);
            } else {
                // Loading a new context turns shuffle off — shuffle never carries
                // across albums/playlists.
                setShuffleOrder(null);
                setIsShuffled(false);
            }
        },
        []
    );

    const resetLoop = useCallback(() => setLoopMode(LoopMode.Off), []);

    const resetShuffle = useCallback(() => {
        setIsShuffled(false);
        setShuffleOrder(null);
        // Drop the queue-identity signature so the next setQueue is treated as a
        // fresh context rather than the same album re-supplied. Without this,
        // reopening the just-closed album via the Shuffle CTA hits the
        // isSameContext early-return and never rebuilds the shuffle order
        // (EVE-11935).
        queueSignatureRef.current = '';
    }, []);

    const cycleLoop = useCallback(() => {
        setLoopMode((mode) => {
            const upcoming = nextLoopMode(mode, loopCapability);
            // EVE-11931: shuffle and Loop-One are mutually exclusive — cycling into
            // Loop-One turns shuffle off (parity with iOS/Android + PRD). These
            // setters are idempotent, so React's Strict-Mode double-invoke of the
            // updater is harmless.
            if (upcoming === LoopMode.One) {
                setIsShuffled(false);
                setShuffleOrder(null);
            }
            return upcoming;
        });
    }, [loopCapability]);

    const toggleShuffle = useCallback(() => {
        if (queue.length === 0) return;
        if (isShuffled) {
            // Disable: drop the permutation so `next`/`previous` fall back to the
            // original `queue` order. The current track keeps playing in place.
            setIsShuffled(false);
            setShuffleOrder(null);
            return;
        }
        // Enable: move the current track to the start of the shuffle order and
        // randomise the rest after it, so playback is not interrupted (Android
        // parity) and "next" can walk every remaining track even when shuffle is
        // enabled on the last track (EVE-13081). Re-enabling always builds a fresh
        // random order (EVE-11931 AC#4). Bounds-check the synced index — it can be
        // stale/out of range right after setQueue, before the active-player sync
        // effect runs — and fall back to 0.
        const synced = currentIndexRef.current;
        const base = synced >= 0 && synced < queue.length ? synced : 0;
        setShuffleOrder(buildShuffleOrder(queue.length, base));
        setIsShuffled(true);
        // Mutually exclusive with Loop-One: enabling shuffle turns Loop-One off.
        if (loopMode === LoopMode.One) setLoopMode(LoopMode.Off);
    }, [queue.length, isShuffled, loopMode]);

    // --- Navigation -------------------------------------------------------
    // `next`/`previous` are PURE: they only read state and return a target queue
    // index (or null). They never mutate, so they are safe to call during render
    // (e.g. to predict whether the player will advance when the track ends).

    const next = useCallback(
        (index: number): number | null => {
            if (queue.length === 0 || index < 0) return null;
            if (!isShuffled || !shuffleOrder) {
                // Linear: step forward through the original queue order; wrap to
                // the first track only when Loop-All is on, otherwise stop.
                if (index < queue.length - 1) return index + 1;
                return loopMode === LoopMode.All ? 0 : null;
            }
            // Shuffled: step forward within the shuffle permutation.
            const pos = shuffleOrder.indexOf(index);
            if (pos < 0) return null;
            if (pos < shuffleOrder.length - 1) return shuffleOrder[pos + 1];
            // At the end of the shuffled order. Stay PURE here: for Loop-All we
            // just report "there is a next track" (the existing first one); the
            // actual re-randomise is the side effect in `advanceNext`.
            return loopMode === LoopMode.All ? shuffleOrder[0] : null;
        },
        [queue.length, loopMode, isShuffled, shuffleOrder]
    );

    const previous = useCallback(
        (index: number): number | null => {
            if (queue.length === 0 || index < 0) return null;
            if (!isShuffled || !shuffleOrder) {
                // Linear: step back through the original queue order; wrap to the
                // last track only when Loop-All is on, otherwise stop (mirrors next).
                if (index > 0) return index - 1;
                return loopMode === LoopMode.All ? queue.length - 1 : null;
            }
            // Shuffled: step back within the shuffle permutation; wrap to the last
            // of the order only when Loop-All is on, otherwise stop (mirrors next).
            const pos = shuffleOrder.indexOf(index);
            if (pos < 0) return null;
            if (pos > 0) return shuffleOrder[pos - 1];
            return loopMode === LoopMode.All
                ? shuffleOrder[shuffleOrder.length - 1]
                : null;
        },
        [queue.length, loopMode, isShuffled, shuffleOrder]
    );

    const advanceNext = useCallback(
        (index: number): number | null => {
            // When not shuffled (or no order yet) this is exactly `next` — no
            // side effect. The shuffle-specific re-randomise lives below.
            if (
                !isShuffled ||
                !shuffleOrder ||
                queue.length === 0 ||
                index < 0
            ) {
                return next(index);
            }
            const pos = shuffleOrder.indexOf(index);
            if (pos < 0) return null;
            if (pos < shuffleOrder.length - 1) return shuffleOrder[pos + 1];
            // End of the shuffled order with Loop-All on: re-randomise into a NEW
            // order and jump to its first track (EVE-11931 / PRD "Shuffled Looping Playback –
            // Full"). `queue` (the original order) is left untouched.
            if (loopMode === LoopMode.All) {
                const fresh = buildFullShuffleOrder(queue.length);
                setShuffleOrder(fresh);
                return fresh[0];
            }
            return null;
        },
        [isShuffled, shuffleOrder, queue.length, loopMode, next]
    );

    const value = useMemo<GuideQueueState>(
        () => ({
            queue,
            loopMode,
            loopCapability,
            isShuffled,
            isShuffleSupported,
            setQueue,
            setCurrentIndex,
            resetLoop,
            resetShuffle,
            cycleLoop,
            toggleShuffle,
            next,
            previous,
            advanceNext,
        }),
        [
            queue,
            loopMode,
            loopCapability,
            isShuffled,
            isShuffleSupported,
            setQueue,
            setCurrentIndex,
            resetLoop,
            resetShuffle,
            cycleLoop,
            toggleShuffle,
            next,
            previous,
            advanceNext,
        ]
    );

    return (
        <GuideQueueContext.Provider value={value}>
            {children}
        </GuideQueueContext.Provider>
    );
};
